import React, { useRef, useState, useEffect } from "react";
import prodigy from "../../Assets/brand/ProdiogyIcon.svg";
import backArrow from "../../Assets/icons/backArrow.svg";
import QuestionMarkCircle from "../../Assets/icons/QuestionMarkCircle.svg";
import { useLocation, useNavigate } from "react-router-dom";

const OTP_LENGTH = 6;
const INITIAL_TIMER = 60;

const FirstVerify: React.FC = () => {
  const email = useLocation().state?.email || "";
  const navigate = useNavigate();
  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(""));
  const [timer, setTimer] = useState(INITIAL_TIMER);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const inputsRef = useRef<HTMLInputElement[]>([]);

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  const handleChange = (value: string, index: number) => {
    if (!/^\d?$/.test(value)) return;

    const updatedOtp = [...otp];
    updatedOtp[index] = value;
    setOtp(updatedOtp);

    if (value && index < OTP_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();
    if (!/^\d{6}$/.test(pastedData)) return;

    const digits = pastedData.split("");
    setOtp(digits);
    inputsRef.current[OTP_LENGTH - 1]?.focus();
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (otp.some((digit) => digit === "")) return;

    setIsSubmitting(true);
    try {
      const otpCode = otp.join("");
      // Navigate to next page on success with email and OTP code
      navigate("/reset-password", {
        state: { email, code: otpCode },
      });
    } catch (error) {
      console.error("Error verifying:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (timer > 0) return;

    setIsResending(true);
    try {
      // Handle resend OTP logic here
      setTimer(INITIAL_TIMER);
      setOtp(Array(OTP_LENGTH).fill(""));
      inputsRef.current[0]?.focus();
    } catch (error) {
      console.error("Error resending OTP:", error);
    } finally {
      setIsResending(false);
    }
  };

  const formatTimer = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const isOtpComplete = otp.every((digit) => digit !== "");

  return (
    <div
      className="w-full max-w-md h-full flex flex-col"
      id="first-verify-container"
    >
      <div
        className="flex items-center justify-between mb-10 mt-10"
        id="first-verify-header"
      >
        <button
          type="button"
          className="text-[14px] font-medium text-[#080F1A] font-outfit leading-[150%] flex items-center gap-2 "
          onClick={() => navigate(-1)}
          aria-label="Go back to previous page"
          id="first-verify-back-button"
        >
          <img
            src={backArrow}
            alt=""
            aria-hidden="true"
            id="first-verify-back-arrow"
          />
          Go Back
        </button>
        <button
          type="button"
          className="focus:outline-none focus:ring-2 focus:ring-[#0566FF] rounded"
          aria-label="Help"
          id="first-verify-help-button"
        >
          <img
            src={QuestionMarkCircle}
            alt="Help"
            id="first-verify-help-icon"
          />
        </button>
      </div>

      <div
        className="flex-1 flex flex-col justify-center"
        id="first-verify-content-wrapper"
      >
        <img
          src={prodigy}
          alt="Prodigy Health"
          className="w-32 md:w-40 mb-6"
          id="first-verify-logo"
        />

        <h2
          className="text-[18px] font-medium text-[var(--Text-Black,#080F1A)] font-outfit leading-[120%] mb-2"
          id="first-verify-heading"
        >
          First, verify it’s you
        </h2>

        <p
          className="text-[14px] font-normal text-[var(--Text-Light-Grey,#647491)] font-outfit leading-[150%] mb-8"
          id="first-verify-description"
        >
          Enter the 6-digit OTP sent to {email}&nbsp;
          <button
            type="button"
            onClick={() => navigate("/email-verification")}
            className="text-[14px] font-medium text-[#0566FF] font-outfit leading-[150%] hover:underline focus:outline-none focus:ring-2 focus:ring-[#0566FF] rounded"
            aria-label="Change email address"
            id="first-verify-change-email-button"
          >
            Change Email
          </button>
        </p>

        <form
          onSubmit={handleSubmit}
          className="flex flex-col"
          id="first-verify-form"
        >
          <div className="flex flex-col mb-4" id="first-verify-input-wrapper">
            <label
              htmlFor="first-verify-input-0"
              className="sr-only"
              id="first-verify-label"
            >
              Enter 6-digit verification code
            </label>
            <div className="flex gap-3" id="first-verify-otp-container">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  id={`first-verify-input-${index}`}
                  ref={(el) => {
                    if (el) inputsRef.current[index] = el;
                  }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(e.target.value, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  onPaste={index === 0 ? handlePaste : undefined}
                  className="w-12 h-12 text-center text-lg rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#0566FF] focus:border-transparent"
                  aria-label={`Digit ${index + 1} of verification code`}
                />
              ))}
            </div>
          </div>

          <div className="mb-6" id="first-verify-resend-wrapper">
            <p
              className="text-[14px] font-normal text-[#647491] font-outfit leading-[150%]"
              id="first-verify-resend-text"
            >
              Didn't get OTP?{" "}
              <button
                type="button"
                onClick={handleResend}
                disabled={timer > 0 || isResending}
                className="text-[14px] font-medium text-[#0566FF] font-outfit leading-[150%] hover:underline focus:outline-none focus:ring-2 focus:ring-[#0566FF] rounded disabled:text-gray-400 disabled:cursor-not-allowed disabled:no-underline"
                aria-label={
                  timer > 0
                    ? `Resend OTP in ${formatTimer(timer)}`
                    : "Resend OTP"
                }
                id="first-verify-resend-button"
              >
                {isResending
                  ? "Resending..."
                  : timer > 0
                    ? `Resend (${formatTimer(timer)})`
                    : "Resend"}
              </button>
            </p>
          </div>

          <button
            type="submit"
            disabled={!isOtpComplete || isSubmitting}
            className="w-full bg-[#0566FF] text-white text-center text-[14px] font-medium font-outfit leading-[150%] py-3 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Verify"
            id="first-verify-submit-button"
          >
            {isSubmitting ? "Verifying..." : "Verify"}
          </button>
        </form>
      </div>

      <p
        className="mt-auto mb-10 text-xs text-[#647491]"
        id="first-verify-footer"
      >
        © 2025 Arete Healthtech Pvt. Ltd. All rights reserved.
      </p>
    </div>
  );
};

export default FirstVerify;
